<app-student-nav></app-student-nav>

<div class="ph-page">
  <div class="network-page">
    <!-- Sidebar -->
    <aside class="network-sidebar" aria-label="Network overview">
      <div class="network-overview-card">
        <h3 class="section-title" style="font-size: 20px; margin-bottom: 18px">Network Overview</h3>
        <div class="overview-stats" role="list" aria-label="Network statistics">
          <div
            class="stat"
            role="listitem"
            (click)="navigateView('my-network')"
            [attr.aria-label]="allConnections.length + ' connections'"
          >
            <div class="stat-value" aria-hidden="true">{{ allConnections.length }}</div>
            <div class="stat-label" aria-hidden="true">Connections</div>
          </div>
          <div
            class="stat"
            role="listitem"
            (click)="navigateView('grow-network')"
            [attr.aria-label]="incomingRequests.length + ' pending requests'"
          >
            <div class="stat-value" aria-hidden="true">{{ incomingRequests.length }}</div>
            <div class="stat-label" aria-hidden="true">Requests</div>
          </div>
          <div
            class="stat"
            role="listitem"
            (click)="navigateView('grow-network')"
            [attr.aria-label]="sentRequests.length + ' invites sent'"
          >
            <div class="stat-value" aria-hidden="true">{{ sentRequests.length }}</div>
            <div class="stat-label" aria-hidden="true">Invites Sent</div>
          </div>
        </div>
      </div>

      <nav class="network-nav" aria-label="Network views">
        <button
          class="network-nav-btn"
          [class.active]="activeView === 'my-network'"
          (click)="switchView('my-network')"
          [attr.aria-pressed]="activeView === 'my-network'"
          aria-label="View my network"
        >
          My Network
        </button>
        <button
          class="network-nav-btn"
          [class.active]="activeView === 'grow-network'"
          (click)="switchView('grow-network')"
          [attr.aria-pressed]="activeView === 'grow-network'"
          aria-label="View grow my network"
        >
          Grow My Network
        </button>
      </nav>
    </aside>

    <!-- Main content -->
    <main class="network-content" aria-label="Network content">
      @if (error) {
        <div class="error-banner" role="alert">{{ error }}</div>
      }
      @if (loading) {
        <div class="loading-state" role="status" aria-live="polite">Loading...</div>
      }

      <!-- My Network -->
      @if (!loading && activeView === 'my-network') {
        <div>
          <div class="network-content-header">
            <h2 class="section-title">Network</h2>
            <div class="search-container">
              <input
                type="text"
                class="search-input"
                placeholder="Search connections..."
                [(ngModel)]="searchQuery"
                (input)="searchConnections()"
                autocomplete="off"
              />
            </div>
          </div>
          <div class="connections-list" role="list" aria-label="Your connections">
            @for (connection of connections; track connection.id) {
              <div
                class="connection-card"
                role="listitem"
                [attr.data-testid]="'connection-card-' + connection.id"
              >
                @if (getOtherUser(connection); as other) {
                  <app-user-link [userId]="other.id" [decorative]="true">
                    <div class="connection-avatar" aria-hidden="true">
                      <app-user-avatar
                        [imageUrl]="other.Student?.Profile?.profile_picture_path"
                        [name]="other.first_name + ' ' + other.last_name"
                        [color]="getAvatarColor(other)"
                      />
                    </div>
                  </app-user-link>
                  <div class="connection-info">
                    <div class="connection-name">
                      <app-user-link [userId]="other.id"
                        >{{ other.preferredName || other.first_name }}
                        {{ other.last_name }}</app-user-link
                      >
                    </div>
                    <div class="connection-role">
                      {{ other.Student?.School?.name }} · Grade {{ other.Student?.grade_level }}
                    </div>
                  </div>
                  <div class="connection-actions">
                    <button
                      class="connection-btn-message"
                      (click)="openChat(connection)"
                      [attr.aria-label]="
                        'Message ' +
                        (other.preferredName || other.first_name) +
                        ' ' +
                        other.last_name
                      "
                    >
                      Message
                    </button>
                    <button
                      class="connection-btn-remove"
                      [class.locked]="isLocked(connection)"
                      [disabled]="isLocked(connection)"
                      (click)="removeConnection(connection.id)"
                      [attr.aria-label]="
                        'Remove ' +
                        (other.preferredName || other.first_name) +
                        ' ' +
                        other.last_name +
                        ' from connections'
                      "
                      [attr.data-testid]="'remove-btn-' + connection.id"
                    >
                      Remove
                    </button>
                  </div>
                }
              </div>
            }

            @if (connections.length === 0 && !searchQuery.trim()) {
              <p class="empty-state" role="status">
                You have no connections yet. Go to Grow My Network to get started.
              </p>
            }
            @if (connections.length === 0 && searchQuery.trim()) {
              <p class="empty-state">No connections match "{{ searchQuery }}".</p>
            }
          </div>
        </div>
      }

      <!-- Grow My Network  -->
      @if (!loading && activeView === 'grow-network') {
        <div>
          <div class="network-content-header">
            <h2 class="section-title">Grow My Network</h2>
          </div>

          <!-- Connection Requests -->
          <div class="grow-section">
            <p class="grow-section-label">Connection Requests</p>
            <div class="connections-list" role="list" aria-label="Incoming connection requests">
              @for (request of incomingRequests; track request.id) {
                <div
                  class="connection-card"
                  role="listitem"
                  [attr.data-testid]="'request-card-' + request.id"
                >
                  <app-user-link [userId]="request.requester?.id" [decorative]="true">
                    <div class="connection-avatar" aria-hidden="true">
                      <app-user-avatar
                        [imageUrl]="request.requester?.Student?.Profile?.profile_picture_path"
                        [name]="
                          (request.requester?.first_name ?? '') +
                          ' ' +
                          (request.requester?.last_name ?? '')
                        "
                        [color]="getAvatarColor(request.requester)"
                      />
                    </div>
                  </app-user-link>
                  <div class="connection-info">
                    <div class="connection-name">
                      <app-user-link [userId]="request.requester?.id"
                        >{{ request.requester?.preferredName || request.requester?.first_name }}
                        {{ request.requester?.last_name }}</app-user-link
                      >
                    </div>
                    <div class="connection-role">
                      {{ request.requester?.Student?.School?.name }} · Grade
                      {{ request.requester?.Student?.grade_level }}
                    </div>
                  </div>
                  <div class="connection-actions">
                    <button
                      class="connection-btn-accept"
                      (click)="acceptRequest(request.id)"
                      [attr.aria-label]="
                        'Accept connection request from ' +
                        (request.requester?.preferredName || request.requester?.first_name) +
                        ' ' +
                        request.requester?.last_name
                      "
                      [attr.data-testid]="'accept-btn-' + request.id"
                    >
                      Accept
                    </button>
                    <button
                      class="connection-btn-decline"
                      (click)="rejectRequest(request.id)"
                      [attr.aria-label]="
                        'Decline connection request from ' +
                        (request.requester?.preferredName || request.requester?.first_name) +
                        ' ' +
                        request.requester?.last_name
                      "
                      [attr.data-testid]="'reject-btn-' + request.id"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              }

              @if (incomingRequests.length === 0) {
                <p class="empty-state" role="status">No pending requests.</p>
              }
            </div>
          </div>

          <!-- Recommended for You -->
          <div class="grow-section" style="margin-top: 32px">
            <p class="grow-section-label">Recommended for You</p>
            <div class="recommended-grid" role="list" aria-label="Recommended connections">
              @for (student of recommended; track student.id) {
                <div
                  class="recommend-card"
                  role="listitem"
                  [attr.data-testid]="'recommend-card-' + student.id"
                >
                  <app-user-link [userId]="student.user_id" [decorative]="true">
                    <div class="recommend-avatar" aria-hidden="true">
                      <app-user-avatar
                        [imageUrl]="student.Profile?.profile_picture_path"
                        [name]="
                          (student.user?.first_name ?? '') + ' ' + (student.user?.last_name ?? '')
                        "
                        [color]="getAvatarColor(student.user)"
                      />
                    </div>
                  </app-user-link>
                  <div class="recommend-name">
                    <app-user-link [userId]="student.user_id"
                      >{{ student.user?.preferredName || student.user?.first_name }}
                      {{ student.user?.last_name }}</app-user-link
                    >
                  </div>
                  <div class="recommend-role">
                    {{ student.School?.name }} · Grade {{ student.grade_level }}
                  </div>
                  <button
                    class="connection-btn-connect"
                    (click)="sendRequest(student.user_id)"
                    [attr.aria-label]="
                      'Connect with ' +
                      (student.user?.preferredName || student.user?.first_name) +
                      ' ' +
                      student.user?.last_name
                    "
                    [attr.data-testid]="'connect-btn-' + student.id"
                  >
                    Connect
                  </button>
                </div>
              }

              @if (recommended.length === 0) {
                <p class="empty-state" role="status">No recommendations right now.</p>
              }
            </div>
          </div>

          <!-- Sent Requests -->
          <div class="grow-section" style="margin-top: 32px">
            <p class="grow-section-label">Outgoing Requests</p>
            <div class="connections-list" role="list" aria-label="Outgoing connection requests">
              @for (sent of sentRequests; track sent.id) {
                <div
                  class="connection-card"
                  role="listitem"
                  [attr.data-testid]="'sent-card-' + sent.id"
                >
                  <app-user-link [userId]="sent.addressee?.id" [decorative]="true">
                    <div class="connection-avatar" aria-hidden="true">
                      <app-user-avatar
                        [imageUrl]="sent.addressee?.Student?.Profile?.profile_picture_path"
                        [name]="
                          (sent.addressee?.first_name ?? '') +
                          ' ' +
                          (sent.addressee?.last_name ?? '')
                        "
                        [color]="getAvatarColor(sent.addressee)"
                      />
                    </div>
                  </app-user-link>
                  <div class="connection-info">
                    <div class="connection-name">
                      <app-user-link [userId]="sent.addressee?.id"
                        >{{ sent.addressee?.preferredName || sent.addressee?.first_name }}
                        {{ sent.addressee?.last_name }}</app-user-link
                      >
                    </div>
                    <div class="connection-role">
                      {{ sent.addressee?.Student?.School?.name }} · Grade
                      {{ sent.addressee?.Student?.grade_level }}
                    </div>
                  </div>
                  <div class="connection-actions">
                    <button
                      class="connection-btn-pending"
                      disabled
                      [attr.aria-label]="
                        'Connection request to ' +
                        (sent.addressee?.preferredName || sent.addressee?.first_name) +
                        ' ' +
                        sent.addressee?.last_name +
                        ' is pending'
                      "
                    >
                      Pending
                    </button>
                  </div>
                </div>
              }

              @if (sentRequests.length === 0) {
                <p class="empty-state" role="status">No outgoing requests.</p>
              }
            </div>
          </div>
        </div>
      }
    </main>
  </div>
</div>

<app-messaging-widget [chatToOpen]="pendingChat"></app-messaging-widget>
